/**
 * CRM visit API — POST {API_BASE_URL}/api/visits/track-visit
 *
 * Body shape (matches vectors.daiv.co.in):
 *   source, page, userId, sessionId, eventType, poojaSlug, amount
 *
 * Funnel (route-based):
 *   page_view        — first page in visit (localStorage landing; 1h + same-day rules)
 *   booking_start    — /pooja/:slug/book (lead; amount from package / ?amount=)
 *   payment_success  — /thank-you/:slug[/:bookingId] (amount from payment flow)
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';
import { apiFetch } from '../utils/api';
import { extractPoojaSlugFromPathname } from '../utils/poojaRoutes';
import { getUtmParams } from '../utils/utm';

const SOURCE_STORAGE_KEY = 'daiv_source';
const SESSION_STORAGE_KEY = 'sessionId';
const LAST_VISIT_STORAGE_KEY = 'lastVisit';
const SESSION_STARTED_MS_KEY = 'daiv_session_start_ms';
const SESSION_LANDING_PAGE_KEY = 'daiv_session_landing_page';
const SESSION_LAND_DATE_KEY = 'daiv_session_land_date';
const SESSION_TIMEOUT_MS = 60 * 60 * 1000;
const VISIT_START_SENT_KEY = 'daiv_trk_visit_start_sent';
const PAYMENT_SUCCESS_SENT_PREFIX = 'daiv_trk_pay_sent_';

export const TRACK_AMOUNT_LEAD_KEY = 'daiv_trk_lead_amount';
export const TRACK_AMOUNT_PAYMENT_KEY = 'daiv_trk_payment_amount';

/** API eventType values (backend / Postman contract). */
export const API_TRACK_EVENT = {
  page_view: 'page_view',
  booking_start: 'booking_start',
  payment_success: 'payment_success',
};

/** Internal funnel keys → API eventType */
export const TRACK_EVENT = {
  visit_start: 'visit_start',
  booking_start: 'booking_start',
  payment_success: 'payment_success',
};

const API_EVENT_BY_FUNNEL = {
  [TRACK_EVENT.visit_start]: API_TRACK_EVENT.page_view,
  [TRACK_EVENT.booking_start]: API_TRACK_EVENT.booking_start,
  [TRACK_EVENT.payment_success]: API_TRACK_EVENT.payment_success,
};

let pendingBeaconQueue = [];

export function setTrackLeadAmount(amount) {
  if (typeof window === 'undefined') return;
  const n = Number(amount);
  if (!Number.isFinite(n) || n < 0) return;
  try {
    sessionStorage.setItem(TRACK_AMOUNT_LEAD_KEY, String(n));
  } catch {
    // ignore
  }
}

export function setTrackPaymentAmount(amount) {
  if (typeof window === 'undefined') return;
  const n = Number(amount);
  if (!Number.isFinite(n) || n < 0) return;
  try {
    sessionStorage.setItem(TRACK_AMOUNT_PAYMENT_KEY, String(n));
  } catch {
    // ignore
  }
}

function tryLockFirstSource(value) {
  if (typeof window === 'undefined' || value == null || String(value).trim() === '') return;
  const v = String(value).trim();
  try {
    if (localStorage.getItem(SOURCE_STORAGE_KEY) != null && localStorage.getItem(SOURCE_STORAGE_KEY) !== '') {
      return;
    }
    localStorage.setItem(SOURCE_STORAGE_KEY, v);
  } catch {
    // ignore
  }
}

function getExternalReferrerLabel() {
  if (typeof document === 'undefined' || !document.referrer) return '';
  try {
    const here = new URL(window.location.href);
    const ref = new URL(document.referrer);
    if (ref.hostname === here.hostname) return '';
    const host = ref.hostname.replace(/^www\./i, '');
    return host || '';
  } catch {
    return '';
  }
}

function getLockedSource() {
  if (typeof window === 'undefined') return 'direct';
  try {
    const locked = localStorage.getItem(SOURCE_STORAGE_KEY);
    if (locked != null && locked !== '') return locked;
  } catch {
    // fall through
  }
  const params = new URLSearchParams(window.location?.search || '');
  const fromUrl = params.get('utm_source');
  if (fromUrl) {
    const v = fromUrl.trim();
    tryLockFirstSource(v);
    return v;
  }
  const { utm_source } = getUtmParams();
  if (utm_source) {
    const v = utm_source.trim();
    tryLockFirstSource(v);
    return v;
  }
  const refLabel = getExternalReferrerLabel();
  if (refLabel) {
    tryLockFirstSource(refLabel);
    return refLabel;
  }
  tryLockFirstSource('direct');
  return 'direct';
}

function getCurrentPagePath() {
  if (typeof window === 'undefined') return '/';
  const path = window.location.pathname || '/';
  const search = window.location.search || '';
  const full = `${path}${search}`;
  return full || '/';
}

function getLocalDateYmd(date = new Date()) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function shouldStartNewVisitSession(now) {
  try {
    const id = localStorage.getItem(SESSION_STORAGE_KEY);
    const lastVisitRaw = localStorage.getItem(LAST_VISIT_STORAGE_KEY);
    const landDate = localStorage.getItem(SESSION_LAND_DATE_KEY);
    if (!id || !lastVisitRaw) return true;
    const lastVisit = parseInt(lastVisitRaw, 10);
    if (Number.isNaN(lastVisit)) return true;
    if (getLocalDateYmd() !== landDate) return true;
    if (now - lastVisit > SESSION_TIMEOUT_MS) return true;
    return false;
  } catch {
    return true;
  }
}

function getSessionLandingPage() {
  try {
    const stored = localStorage.getItem(SESSION_LANDING_PAGE_KEY);
    if (stored != null && String(stored).trim() !== '') return String(stored).trim();
  } catch {
    // ignore
  }
  return getCurrentPagePath();
}

function beginNewVisitSession(now) {
  const id =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `${now}-${Math.random().toString(36).slice(2, 11)}`;
  const landing = getCurrentPagePath();
  try {
    localStorage.setItem(SESSION_STORAGE_KEY, id);
    localStorage.setItem(LAST_VISIT_STORAGE_KEY, String(now));
    localStorage.setItem(SESSION_STARTED_MS_KEY, String(now));
    localStorage.setItem(SESSION_LANDING_PAGE_KEY, landing);
    localStorage.setItem(SESSION_LAND_DATE_KEY, getLocalDateYmd(new Date(now)));
    localStorage.removeItem(VISIT_START_SENT_KEY);
  } catch {
    // ignore
  }
  return id;
}

function getOrCreateSessionId() {
  if (typeof window === 'undefined') return '';
  const now = Date.now();
  try {
    if (shouldStartNewVisitSession(now)) {
      return beginNewVisitSession(now);
    }
    localStorage.setItem(LAST_VISIT_STORAGE_KEY, String(now));
    const id = localStorage.getItem(SESSION_STORAGE_KEY);
    return id || beginNewVisitSession(now);
  } catch {
    return beginNewVisitSession(now);
  }
}

/** `/pooja/:slug/book` or `/pooja/live/:slug/book` → slug */
export function extractPoojaSlugFromPath(path) {
  if (!path || typeof path !== 'string') return undefined;
  const pathname = path.split('?')[0];
  return extractPoojaSlugFromPathname(pathname);
}

/** `/pooja/:slug` or `/pooja/live/:slug` (detail, not /book) → slug */
export function extractPoojaDetailSlugFromPathname(pathname) {
  return extractPoojaSlugFromPathname(pathname);
}

/** `/thank-you/:slug` or `/thank-you/:slug/:bookingId` → slug */
export function extractThankYouSlugFromPath(path) {
  if (!path || typeof path !== 'string') return undefined;
  const pathname = path.split('?')[0];
  const m = /^\/thank-you\/([^/]+)(?:\/([^/]+))?\/?$/.exec(pathname);
  return m ? m[1] : undefined;
}

export function extractThankYouBookingIdFromPath(path) {
  if (!path || typeof path !== 'string') return undefined;
  const pathname = path.split('?')[0];
  const m = /^\/thank-you\/[^/]+\/([^/]+)\/?$/.exec(pathname);
  return m ? m[1] : undefined;
}

function resolvePoojaSlug(pathname, page) {
  return (
    extractPoojaSlugFromPath(page || pathname) ||
    extractThankYouSlugFromPath(page || pathname) ||
    extractPoojaDetailSlugFromPathname(pathname) ||
    null
  );
}

function parseAmountFromPageQuery(page) {
  if (!page || typeof page !== 'string') return null;
  try {
    const idx = page.indexOf('?');
    if (idx < 0) return null;
    const params = new URLSearchParams(page.slice(idx + 1));
    const raw = params.get('amount');
    if (raw == null || raw === '') return null;
    const n = Number(raw);
    if (Number.isFinite(n) && n >= 0) return n;
  } catch {
    // ignore
  }
  return null;
}

function readStoredAmount(key) {
  try {
    const raw = sessionStorage.getItem(key);
    if (raw == null || raw === '') return null;
    const n = Number(raw);
    if (Number.isFinite(n) && n >= 0) return n;
  } catch {
    // ignore
  }
  return null;
}

function resolveAmountForEvent(funnelEventType, page) {
  const fromQuery = parseAmountFromPageQuery(page);
  if (funnelEventType === TRACK_EVENT.booking_start) {
    return readStoredAmount(TRACK_AMOUNT_LEAD_KEY) ?? fromQuery ?? 0;
  }
  if (funnelEventType === TRACK_EVENT.payment_success) {
    return readStoredAmount(TRACK_AMOUNT_PAYMENT_KEY) ?? readStoredAmount(TRACK_AMOUNT_LEAD_KEY) ?? fromQuery ?? 0;
  }
  return fromQuery ?? 0;
}

function isThankYouPagePath(pathname) {
  return /^\/thank-you\/[^/]+/.test(pathname || '');
}

function sessionFlagOnce(key) {
  try {
    if (sessionStorage.getItem(key) === '1') return false;
    sessionStorage.setItem(key, '1');
    return true;
  } catch {
    return true;
  }
}

/** POST body exactly as CRM API expects (camelCase). */
function buildApiPayload({ sessionId, source, page, userId, eventType, poojaSlug, amount }) {
  return {
    source,
    page,
    userId: userId ?? null,
    sessionId,
    eventType,
    poojaSlug: poojaSlug || null,
    amount: Number.isFinite(Number(amount)) && Number(amount) >= 0 ? Number(amount) : 0,
  };
}

function buildPayload(event) {
  const sessionId = getOrCreateSessionId();
  const source = getLockedSource();
  const funnelType = event.eventType;
  const apiEventType = API_EVENT_BY_FUNNEL[funnelType];
  if (!apiEventType) return null;

  const page =
    funnelType === TRACK_EVENT.visit_start
      ? (event.page && String(event.page).trim()) || getSessionLandingPage()
      : event.page || (typeof window !== 'undefined' ? getCurrentPagePath() : '/');

  const pathname = page.split('?')[0];
  const poojaSlug = event.poojaSlug ?? event.pooja_slug ?? resolvePoojaSlug(pathname, page);
  const amount = event.amount != null && !Number.isNaN(Number(event.amount))
    ? Number(event.amount)
    : resolveAmountForEvent(funnelType, page);

  return buildApiPayload({
    sessionId,
    source,
    page,
    userId: event.userId ?? null,
    eventType: apiEventType,
    poojaSlug,
    amount,
  });
}

const trackUrl = () => API_ENDPOINTS.trackVisit;

async function sendPayloadReliable(payload, { isPayment = false } = {}) {
  const url = trackUrl();
  const body = JSON.stringify(payload);

  let httpOk = false;
  let serverAnswered = false;
  try {
    const res = await apiFetch(url, {
      method: 'POST',
      body,
    });
    httpOk = res.ok;
    serverAnswered = true;
  } catch {
    httpOk = false;
  }

  if (httpOk) return;
  /* A 4xx/5xx means the server is up but rejecting: resending only multiplies the failures.
     Payment events still retry (revenue attribution matters more than log noise). */
  if (serverAnswered && !isPayment) return;

  try {
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      if (navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }))) return;
    }
  } catch {
    // ignore
  }

  try {
    pendingBeaconQueue.push(payload);
  } catch {
    // ignore
  }

  if (isPayment && typeof fetch !== 'undefined') {
    fetch(url, {
      method: 'POST',
      body,
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
    }).catch(() => {});
  }
}

function sendTrackNow(event) {
  const payload = buildPayload(event);
  if (!payload) return;
  const isPayment = payload.eventType === API_TRACK_EVENT.payment_success;
  void sendPayloadReliable(payload, { isPayment });
}

function isEditPersonsPage(page) {
  const query = String(page || '').split('?')[1]?.split('#')[0] || '';
  const edit = new URLSearchParams(query).get('edit');
  const v = String(edit || '').toLowerCase();
  return v === '1' || v === 'true' || v === 'persons';
}

function flushPendingOnHide() {
  const url = trackUrl();
  if (!pendingBeaconQueue.length) return;
  const copy = [...pendingBeaconQueue];
  pendingBeaconQueue = [];
  copy.forEach((payload) => {
    try {
      if (navigator.sendBeacon) {
        navigator.sendBeacon(url, new Blob([JSON.stringify(payload)], { type: 'application/json' }));
      }
    } catch {
      pendingBeaconQueue.push(payload);
    }
  });
}

function trackFunnelForRoute(pathname, page, userId) {
  const sessionId = getOrCreateSessionId();
  if (!sessionId) return;

  const landingPage = getSessionLandingPage();
  const slugFromRoute = resolvePoojaSlug(pathname, page);

  try {
    if (localStorage.getItem(VISIT_START_SENT_KEY) !== sessionId) {
      sendTrackNow({
        eventType: TRACK_EVENT.visit_start,
        page: landingPage,
        userId: userId ?? null,
        poojaSlug: slugFromRoute,
      });
      localStorage.setItem(VISIT_START_SENT_KEY, sessionId);
    }
  } catch {
    sendTrackNow({
      eventType: TRACK_EVENT.visit_start,
      page: landingPage,
      userId: userId ?? null,
      poojaSlug: slugFromRoute,
    });
  }

  /* booking_start is sent from BookingForm once package amount is known (lead + amount). */

  if (isThankYouPagePath(pathname) && !isEditPersonsPage(page)) {
    const slug = extractThankYouSlugFromPath(pathname);
    if (slug) {
      const bookingId = extractThankYouBookingIdFromPath(pathname) || '';
      const key = `${PAYMENT_SUCCESS_SENT_PREFIX}${sessionId}_${slug}_${bookingId}`;
      if (sessionFlagOnce(key)) {
        sendTrackNow({
          eventType: TRACK_EVENT.payment_success,
          page,
          userId: userId ?? null,
          poojaSlug: slug,
        });
      }
    }
  }
}

export function trackEvent(eventType, options = {}) {
  const { page, userId, amount, pooja_slug, poojaSlug } = options;
  const path = typeof window !== 'undefined' ? window.location.pathname : page || '/';
  sendTrackNow({
    page: page || path,
    eventType,
    userId: userId ?? null,
    poojaSlug: poojaSlug ?? pooja_slug,
    amount,
  });
}

export default function useTrackVisit(userId) {
  const { pathname, search } = useLocation();

  useEffect(() => {
    const page = `${pathname}${search || ''}`;
    trackFunnelForRoute(pathname, page, userId);
  }, [pathname, search, userId]);

  useEffect(() => {
    const onHide = () => {
      flushPendingOnHide();
    };
    window.addEventListener('pagehide', onHide);
    return () => window.removeEventListener('pagehide', onHide);
  }, []);
}

export function getSourceForLead() {
  try {
    const v = localStorage.getItem(SOURCE_STORAGE_KEY);
    if (v != null && v !== '') return v;
  } catch {
    // ignore
  }
  return 'direct';
}
